/**
 * @file useOrderTabParam.ts
 * @description Вкладка и подсветка строки карточки заказа живут в URL
 * (phase19.24-order-card-chain-design.md §4).
 *
 * Было: `useState` — ссылку на платёж нельзя переслать коллеге, а F5 сбрасывал
 * вкладку на первую. Стало: `?tab=payments&focus=<id>` — узел «Путь заказа»
 * ведёт прямо в нужную строку, и адрес переживает перезагрузку.
 *
 * Список вкладок — реестр: новая вкладка = запись, а не ещё одна ветка в
 * разметке страницы. Неизвестный `tab` в адресе читается как «Обзор»
 * (карточка открывается на обзоре — решение §11 п.3), а не роняет страницу.
 */

import { useCallback } from "react";
import { useSearchParams } from "react-router";

/** Ключи вкладок карточки. `activity` — «Лента» (события + история стадий). */
export type OrderTabKey =
  | "overview"
  | "items"
  /** Единственный дом условий доставки (phase19.24 шаг 5.2). */
  | "delivery"
  | "shipments"
  | "payments"
  | "returns"
  | "activity";

export interface OrderTabMeta {
  key: OrderTabKey;
  label: string;
}

/**
 * Реестр вкладок по §5. «Оплаты и документы» — одна вкладка: счёт и УПД
 * читают вместе с платежами, а вкладки «Путь» больше нет (её степпер и
 * документы переехали в блок «Путь заказа» над вкладками).
 */
export const ORDER_TABS: OrderTabMeta[] = [
  { key: "overview", label: "Обзор" },
  { key: "items", label: "Позиции" },
  // «Доставка» стоит между составом и отгрузками: сначала что везём, потом на
  // каких условиях, и только затем физика отгрузки (проект шага 5.2 §2).
  { key: "delivery", label: "Доставка" },
  { key: "shipments", label: "Отгрузки" },
  { key: "payments", label: "Оплаты и документы" },
  { key: "returns", label: "Возвраты" },
  { key: "activity", label: "Лента" },
];

const DEFAULT_TAB: OrderTabKey = "overview";

/**
 * Ключ вкладки — из реестра? Нужен там, где ключ пришёл извне: адрес строки и
 * ссылка узла «Пути заказа» (`link.kind === "tab"`) приходят с сервера, и
 * молча подменять неизвестный ключ на «Обзор» нельзя — кнопка уводила бы не
 * туда, а расхождение контрактов никто бы не заметил (находка ревью
 * 2026-09-21).
 */
export function isOrderTabKey(value: string | null | undefined): value is OrderTabKey {
  return !!value && ORDER_TABS.some((t) => t.key === value);
}

export interface OrderTabParam {
  tab: OrderTabKey;
  /** id строки, которую надо раскрыть и обвести (платёж, отгрузка, возврат). */
  focus: string | null;
  /** Переключение вкладки руками: `focus` при этом гасим — он уже отработал. */
  setTab: (key: OrderTabKey) => void;
}

export function useOrderTabParam(): OrderTabParam {
  const [params, setParams] = useSearchParams();
  const raw = params.get("tab");
  const tab = isOrderTabKey(raw) ? raw : DEFAULT_TAB;

  const setTab = useCallback(
    (key: OrderTabKey) => {
      const next = new URLSearchParams(params);
      next.set("tab", key);
      next.delete("focus");
      // replace: переключение вкладок не должно забивать историю браузера —
      // «назад» обязан возвращать в реестр, а не перебирать вкладки.
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  return { tab, focus: params.get("focus"), setTab };
}
